import { ReactNode } from 'react';
import { Tabs, Divider, Tag } from 'antd';
import { useAppSelector } from '@hooks/redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ChangePassword from './ChangePassword';
import ContactInfo from './ContactInfo';
import GoogleAuth from './GoogleAuth';
import {
  LockOutlined,
  SafetyOutlined,
  RightOutlined,
  CreditCardOutlined,
  FileTextOutlined,
  BankOutlined,
  SwapOutlined,
  AppstoreOutlined,
  UserOutlined,
  PhoneOutlined,
  IdcardOutlined,
  MailOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
} from '@ant-design/icons';

function Profile() {
  const { t } = useTranslation();
  const user = useAppSelector(state => state.appReducer.user);
  const permissions = useAppSelector(state => state.userReducer.permissions);
  const navigate = useNavigate();

  const initials =
    [user.first_name, user.last_name]
      .filter(Boolean)
      .map((s: string) => s[0]?.toUpperCase())
      .join('') ||
    user.username?.[0]?.toUpperCase() ||
    'U';

  const navItems = [
    {
      key: 'payments',
      label: t('Payments'),
      desc: t('Payments nav desc'),
      icon: <CreditCardOutlined />,
      path: '/payments/own',
      perm: 'payments',
    },
    {
      key: 'statement',
      label: t('Statement'),
      desc: t('Statement nav desc'),
      icon: <FileTextOutlined />,
      path: '/statement',
      perm: 'statement',
    },
    {
      key: 'accounts',
      label: t('Accounts'),
      desc: t('Accounts nav desc'),
      icon: <BankOutlined />,
      path: '/accounts/bankAccounts',
      perm: null,
    },
    {
      key: 'transactions',
      label: t('Transactions nav label'),
      desc: t('Transactions nav desc'),
      icon: <SwapOutlined />,
      path: '/transactions',
      perm: null,
    },
    {
      key: 'templates',
      label: t('Templates'),
      desc: t('Templates nav desc'),
      icon: <AppstoreOutlined />,
      path: '/templates',
      perm: null,
    },
  ].filter(a => !a.perm || permissions[a.perm]);

  const details = [
    user.username && { icon: <UserOutlined />, label: t('Username'), value: user.username },
    user.mobile_phone && { icon: <PhoneOutlined />, label: t('Phone label'), value: user.mobile_phone },
    user.email && { icon: <MailOutlined />, label: t('Email'), value: user.email },
    user.first_name && {
      icon: <IdcardOutlined />,
      label: t('Name label'),
      value: `${user.first_name} ${user.last_name ?? ''}`.trim(),
    },
  ].filter(Boolean) as { icon: ReactNode; label: string; value: string }[];

  const securityTabs = [
    {
      key: 'google',
      label: (
        <span className='flex items-center gap-1.5'>
          <SafetyOutlined />
          Google Authenticator
        </span>
      ),
      children: (
        <div className='pt-2'>
          <GoogleAuth />
        </div>
      ),
    },
    {
      key: 'password',
      label: (
        <span className='flex items-center gap-1.5'>
          <LockOutlined />
          {t('Change Password')}
        </span>
      ),
      children: (
        <div className='pt-2'>
          <ChangePassword />
        </div>
      ),
    },
    {
      key: 'contacts',
      label: (
        <span className='flex items-center gap-1.5'>
          <MailOutlined />
          {t('Contact info')}
        </span>
      ),
      children: (
        <div className='pt-2'>
          <ContactInfo />
        </div>
      ),
    },
  ];

  return (
    <main className='pb-14 w-full' style={{ background: '#f5f5f5' }}>
      <div className='flex justify-center'>
        <div className='w-full max-lg:px-3 pt-6 pb-8' style={{ maxWidth: 1130, color: '#011957' }}>
          <div className='grid lg:grid-cols-[340px_1fr] grid-cols-1 gap-5 items-start'>
            <div className='flex flex-col gap-5'>
              <div className='bg-white rounded-xl p-6'>
                <div className='flex flex-col items-center text-center'>
                  <div
                    className='w-[72px] h-[72px] rounded-full flex items-center justify-center text-white text-2xl font-semibold'
                    style={{ background: 'linear-gradient(135deg, #011957 0%, #1a5fb4 100%)' }}
                  >
                    {initials}
                  </div>
                  <p className='font-semibold text-base m-0 mt-3'>
                    {[user.first_name, user.last_name].filter(Boolean).join(' ') || user.username}
                  </p>
                  {user.position && <p className='text-sm text-gray-400 m-0 mt-0.5'>{user.position}</p>}
                  <div className='mt-3'>
                    <Tag
                      color={user.is_2fa ? 'success' : 'default'}
                      icon={user.is_2fa ? <CheckCircleFilled /> : <CloseCircleFilled />}
                    >
                      2FA {user.is_2fa ? t('2FA On') : t('2FA Off')}
                    </Tag>
                  </div>
                </div>

                <Divider className='my-4' />

                <div className='space-y-3'>
                  {details.map(d => (
                    <div key={d.label} className='flex items-center gap-3'>
                      <div
                        className='w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0'
                        style={{ background: '#f0f4ff' }}
                      >
                        <span style={{ color: '#011957', fontSize: 13 }}>{d.icon}</span>
                      </div>
                      <div className='min-w-0'>
                        <p className='text-xs text-gray-400 m-0'>{d.label}</p>
                        <p className='text-sm font-medium m-0 truncate'>{d.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {navItems.length > 0 && (
                <div className='bg-white rounded-xl overflow-hidden'>
                  <p className='px-5 pt-4 pb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider m-0'>
                    {t('Quick navigation')}
                  </p>
                  {navItems.map((item, idx) => (
                    <button
                      key={item.key}
                      onClick={() => navigate(item.path)}
                      className='w-full flex items-center justify-between px-5 py-3 border-0 bg-transparent cursor-pointer'
                      style={{
                        borderTop: idx > 0 ? '1px solid #f5f5f5' : 'none',
                        transition: 'background 0.15s',
                        color: '#011957',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#f7f9ff')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <div className='flex items-center gap-3 min-w-0'>
                        <div
                          className='w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0'
                          style={{ background: '#eef2ff' }}
                        >
                          <span style={{ fontSize: 16 }}>{item.icon}</span>
                        </div>
                        <div className='text-left min-w-0'>
                          <p className='text-sm font-medium m-0'>{item.label}</p>
                          <p className='text-xs text-gray-400 m-0 truncate'>{item.desc}</p>
                        </div>
                      </div>
                      <RightOutlined style={{ fontSize: 10, color: '#d1d5db', flexShrink: 0 }} />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className='bg-white rounded-xl px-6 pt-5 pb-6'>
              <p className='font-semibold text-base m-0'>{t('Security')}</p>
              <Tabs items={securityTabs} />
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default Profile;
